import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, type ApiResponse } from '@/api/client';

export interface AdminUser {
  id: number;
  name: string;
  phone: string;
  email: string | null;
  is_engineer: boolean;
  wallet_balance: string;
  image: string | null;
}

interface LoginResponse {
  user: AdminUser;
  token: string;
  role: 'admin' | 'engineer';
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'));
  const user = ref<AdminUser | null>(JSON.parse(localStorage.getItem('user') ?? 'null'));

  const accreditationMissing = ref<boolean | null>(null);
  const accreditationDialogVisible = ref(false);

  async function refreshAccreditation() {
    const res = await api.get<ApiResponse<{ saudi_engineers_accreditation_number: string | null }>>(
      '/api/engineer/profile/accreditation',
    );
    accreditationMissing.value = !res.data.data.saudi_engineers_accreditation_number?.trim();
    if (!accreditationMissing.value) accreditationDialogVisible.value = false;
    return !accreditationMissing.value;
  }

  const isAuthenticated = computed(() => Boolean(token.value));

  async function login(phone: string, password: string, role: 'admin' | 'engineer' = 'admin') {
    const endpoint =
      role === 'engineer' ? '/api/dashboard/auth/engineer-login' : '/api/dashboard/auth/login';
    const res = await api.post<ApiResponse<LoginResponse>>(endpoint, { phone, password });
    accreditationMissing.value = null;
    accreditationDialogVisible.value = false;
    token.value = res.data.data.token;
    user.value = res.data.data.user;
    localStorage.setItem('token', token.value);
    localStorage.setItem('user', JSON.stringify(user.value));
  }

  function logout() {
    accreditationMissing.value = null;
    accreditationDialogVisible.value = false;
    token.value = null;
    user.value = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  return {
    token,
    user,
    isAuthenticated,
    accreditationMissing,
    accreditationDialogVisible,
    refreshAccreditation,
    login,
    logout,
  };
});
